import React from "react";
import {
  ActionButton,
  joinClasses,
} from "../ResultsPrimitives.jsx";
import { resolveDispatchDayStatus } from "../../utils/dispatchDayControls.js";
import {
  buildDispatchCalendarCells,
  buildDispatchMonthGroups,
  buildDispatchStatusPills,
  buildDispatchSummary,
  buildDayAriaLabel,
  buildDaySecondaryLabel,
  buildMonthSummary,
  createDispatchCalendarKeyDownHandler,
  formatCountLabel,
  formatReadableDispatchDate,
  getDayCardClassName,
  getLocalTodayDispatchDate,
  getStateBadgeClassName,
  isDispatchDateOutsideRange,
  normalizeText,
  scrollCalendarMonthTabHorizontally,
  toSafeInteger,
} from "../../utils/dispatchDayCalendarUtils.js";

export {
  buildDispatchCalendarCells,
  buildDispatchMonthGroups,
  buildDispatchStatusPills,
  buildDispatchStatusSummary,
  createDispatchCalendarKeyDownHandler,
  formatReadableDispatchDate,
  getDispatchMonthKey,
  getLocalTodayDispatchDate,
  normalizeDispatchMonthKey,
  resolveDefaultDispatchMonthKey,
} from "../../utils/dispatchDayCalendarUtils.js";

const WEEKDAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
function DispatchStatusPills({ pills = [] }) {
  if (!Array.isArray(pills) || pills.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      {pills.map((pill) => (
        <span
          key={pill.key}
          className={joinClasses([
            "inline-flex items-center rounded-[4px] border px-1.5 py-0.5 text-[0.56rem] font-black uppercase tracking-[0.11em]",
            pill.className,
          ])}
        >
          {pill.label}
        </span>
      ))}
    </div>
  );
}

function DispatchMonthTabs({ activeMonthKey = "", monthGroups = [], onSelectMonthKey = null }) {
  const activeTabRef = React.useRef(null);
  const tabsRef = React.useRef(null);

  React.useEffect(() => {
    scrollCalendarMonthTabHorizontally(activeTabRef.current, tabsRef.current);
  }, [activeMonthKey]);

  if (!Array.isArray(monthGroups) || monthGroups.length <= 1) {
    return null;
  }

  return (
    <div ref={tabsRef} className="-mx-0.5 overflow-x-auto px-0.5 pb-0.5">
      <div className="flex min-w-max gap-1.25">
        {monthGroups.map((group) => (
          <button
            key={group.key}
            ref={group.key === activeMonthKey ? activeTabRef : undefined}
            type="button"
            aria-pressed={group.key === activeMonthKey}
            className={joinClasses([
              "flex shrink-0 items-baseline gap-1.5 rounded-[4px] border px-2 py-1 text-left transition duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
              group.key === activeMonthKey
                ? "border-accent bg-accent text-white shadow-[0_10px_24px_rgba(15,92,87,0.16)]"
                : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
            ])}
            onClick={() => {
              onSelectMonthKey?.(group.key);
            }}
          >
            <span className="text-[0.74rem] font-semibold tracking-[-0.02em]">{group.label}</span>
            <span className="text-[0.58rem] leading-4 text-current/72">{formatCountLabel(group.items.length, "journée")}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function DispatchCalendarMonthPanel({
  activeMonthGroup = null,
  calendarCells = [],
  dayButtonRefs = null,
  minDate = "",
  maxDate = "",
  onSelectDispatchDay = null,
  showLegend = true,
  todayDispatchDate = "",
}) {
  return (
    <div className="grid gap-1 rounded-[4px] border border-slate-200 bg-slate-50 p-1.5">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <p className="m-0 text-[0.66rem] font-black uppercase tracking-[0.14em] text-slate-500">{activeMonthGroup?.label || "Journées disponibles"}</p>
          <p className="m-0 text-[0.65rem] leading-4 text-slate-500">{buildMonthSummary(activeMonthGroup)}</p>
        </div>

        {showLegend ? (
          <div className="flex flex-wrap items-center justify-end gap-1 text-[0.58rem] font-semibold text-slate-500">
            <span className="inline-flex items-center gap-1 rounded-[4px] border border-slate-200 bg-white px-1.5 py-0.5 text-slate-600"><span className="h-1.5 w-1.5 bg-slate-400" aria-hidden="true" />Non optimisée</span>
            <span className="inline-flex items-center gap-1 rounded-[4px] border border-accent/30 bg-white px-1.5 py-0.5 text-accent-strong"><span className="h-1.5 w-1.5 bg-accent" aria-hidden="true" />Optimisée</span>
            <span className="inline-flex items-center gap-1 rounded-[4px] border border-red-200 bg-white px-1.5 py-0.5 text-red-800"><span className="h-1.5 w-1.5 bg-red-700" aria-hidden="true" />Fermée</span>
            <span className="inline-flex items-center gap-1 rounded-[4px] border border-slate-200 bg-white px-1.5 py-0.5 text-slate-500">Auj. = aujourd'hui</span>
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-7 gap-0.75">
        {WEEKDAY_LABELS.map((weekdayLabel) => (
          <span
            key={weekdayLabel}
            className="px-1 text-center text-[0.58rem] font-black uppercase tracking-[0.14em] text-slate-500"
          >
            {weekdayLabel}
          </span>
        ))}

        {calendarCells.map((cell, cellIndex) => {
          if (!cell?.item) {
            return <div key={cell?.key || `empty-${cellIndex}`} className="min-h-12 rounded-[4px]" aria-hidden="true" />;
          }

          const isToday = cell.item.dispatchDate === todayDispatchDate;
          const orderCount = toSafeInteger(cell.item.orderCount);
          const showDayStateBadges = cell.item.isActive || isToday;
          const isOutsideRange = isDispatchDateOutsideRange(cell.item.dispatchDate, { minDate, maxDate });

          return (
            <button
              key={cell.key}
              type="button"
              disabled={isOutsideRange}
              data-calendar-index={cellIndex}
              ref={dayButtonRefs
                ? (element) => {
                  dayButtonRefs.current[cellIndex] = element;
                }
                : undefined}
              aria-current={cell.item.isActive ? "date" : undefined}
              aria-label={buildDayAriaLabel(cell.item, { isToday })}
              className={joinClasses([
                "group flex min-h-12 min-w-0 flex-col rounded-[4px] border px-1 py-0.75 text-left transition duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
                getDayCardClassName(cell.item),
                isOutsideRange ? "disabled:cursor-not-allowed disabled:opacity-40" : "",
                isToday ? "ring-1 ring-[rgba(15,92,87,0.2)] ring-inset" : "",
              ])}
              onClick={() => {
                onSelectDispatchDay?.(cell.item.dispatchDate);
              }}
            >
              <span className="flex flex-wrap items-center justify-between gap-x-0.5">
                <span className="text-[0.88rem] font-semibold leading-4 tracking-[-0.03em] text-current">{cell.dayNumber}</span>
                {showDayStateBadges ? (
                  <span className="flex flex-wrap items-center gap-0.5">
                    {cell.item.isActive ? (
                      <span className="rounded-[4px] border border-slate-200 bg-white px-0.5 text-[0.48rem] font-black uppercase leading-3 text-slate-700">
                        Active
                      </span>
                    ) : null}
                    {isToday ? (
                      <span className="rounded-[4px] border border-accent/30 bg-white px-0.5 text-[0.48rem] font-black uppercase leading-3 text-accent-strong">
                        Auj.
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </span>

              <span className="mt-auto grid">
                <span className="flex flex-wrap items-baseline gap-x-0.5 leading-3 text-current/90">
                  <span className="text-[0.78rem] font-semibold tracking-[-0.03em] text-current">{orderCount}</span>
                  <span className="text-[0.52rem] font-bold uppercase text-current/70">cmd</span>
                </span>
                <span className="text-[0.54rem] leading-3 text-current/80">
                  {buildDaySecondaryLabel(cell.item)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DispatchInlineCalendarPreview({
  activeMonthGroup = null,
  activeMonthKey = "",
  calendarCells = [],
  dispatchStatusPills = [],
  dispatchStatusSummary = null,
  monthGroups = [],
  onSelectDispatchDay = null,
  onSelectMonthKey = null,
  todayDispatchDate = "",
}) {
  if (!activeMonthGroup) {
    return null;
  }

  return (
    <section className="grid min-w-0 gap-1.5 rounded-[4px] border border-slate-200 bg-white p-2 shadow-sm xl:max-w-216 xl:justify-self-start">
      <div className="grid gap-1">
        <p className="m-0 text-[0.64rem] font-black uppercase tracking-[0.16em] text-accent-strong">Calendrier du mois courant</p>

        <DispatchStatusPills pills={dispatchStatusPills} />

        {dispatchStatusSummary?.inconsistent > 0 ? (
          <p className="m-0 text-[0.64rem] leading-5 text-[#9a670f]">
            {formatCountLabel(dispatchStatusSummary.inconsistent, "journée mixte", "journées mixtes")} à vérifier.
          </p>
        ) : null}
      </div>

      <DispatchMonthTabs
        activeMonthKey={activeMonthKey}
        monthGroups={monthGroups}
        onSelectMonthKey={onSelectMonthKey}
      />

      <DispatchCalendarMonthPanel
        activeMonthGroup={activeMonthGroup}
        calendarCells={calendarCells}
        onSelectDispatchDay={onSelectDispatchDay}
        showLegend={false}
        todayDispatchDate={todayDispatchDate}
      />
    </section>
  );
}

export function DispatchDayCalendarPopover({
  activeDate = "",
  activeMonthKey = "",
  align = "left",
  controls = null,
  dayButtonRefs = null,
  isOpen = false,
  isPositioned = true,
  isStackedInlinePreview = false,
  loadingMessage = "",
  errorMessage = "",
  minDate = "",
  maxDate = "",
  floatingStyles = null,
  onCalendarKeyDown = null,
  onClose = null,
  onSelectDate = null,
  onSelectMonthKey = null,
  onToggleArchive = null,
  popoverClassName = "",
  popoverId = "",
  popoverRef = null,
  positioning = "anchored",
  setFloatingRef = null,
  showArchive = false,
  titleId = "",
}) {
  if (!isOpen) {
    return null;
  }

  const items = Array.isArray(controls?.items)
    ? controls.items.filter(Boolean).map((item) => ({
      ...item,
      dispatchDate: normalizeText(item?.dispatchDate),
      label: normalizeText(item?.label),
      isActive: normalizeText(item?.dispatchDate) === normalizeText(activeDate),
    }))
    : [];
  const activeDispatchDate = normalizeText(activeDate);
  const hiddenClosedCount = toSafeInteger(controls?.hiddenClosedCount);
  const selectedLabel = normalizeText(controls?.selectedLabel) || activeDispatchDate || "Journée active";
  const hasExplicitSelection = activeDispatchDate.length > 0;
  const activeItem = hasExplicitSelection
    ? items.find((item) => item.dispatchDate === activeDispatchDate) || null
    : null;
  const monthGroups = React.useMemo(() => buildDispatchMonthGroups(items), [items]);
  const activeMonthGroup = monthGroups.find((group) => group.key === activeMonthKey) || monthGroups[0] || null;
  const calendarCells = React.useMemo(() => buildDispatchCalendarCells(activeMonthGroup), [activeMonthGroup]);
  const selectableCells = calendarCells.map((cell) => (
    isDispatchDateOutsideRange(cell.item?.dispatchDate, { minDate, maxDate }) ? { ...cell, item: null } : cell
  ));
  const todayDispatchDate = React.useMemo(() => getLocalTodayDispatchDate(), []);
  const effectiveHiddenClosedCount = showArchive ? 0 : hiddenClosedCount;
  const activeStatusSource = activeItem || {
    dispatchDayState: normalizeText(controls?.activeDispatchState) || "open",
  };
  const activeStatus = hasExplicitSelection ? resolveDispatchDayStatus(activeStatusSource) : null;
  const activeStatusClassName = activeStatus ? getStateBadgeClassName(activeStatusSource) : "";
  const activeSummary = buildDispatchSummary(activeItem, { hiddenClosedCount: effectiveHiddenClosedCount });
  const dispatchStatusPills = React.useMemo(
    () => buildDispatchStatusPills(items, { hiddenClosedCount, showArchive }),
    [hiddenClosedCount, items, showArchive],
  );
  const handleCalendarKeyDown = onCalendarKeyDown || createDispatchCalendarKeyDownHandler({
    calendarCells: selectableCells,
    dayButtonRefs,
    onClose,
  });
  const isFloatingPositioned = positioning === "floating";
  const resolvedPopoverRef = isFloatingPositioned
    ? (node) => {
      setFloatingRef?.(node);
      if (typeof popoverRef === "function") {
        popoverRef(node);
      } else if (popoverRef && typeof popoverRef === "object") {
        popoverRef.current = node;
      }
    }
    : popoverRef;

  return (
    <div
      id={popoverId}
      ref={resolvedPopoverRef}
      data-dispatch-day-calendar-popover="true"
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      className={joinClasses([
        "z-popover grid w-[min(38rem,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-1.5rem)] gap-1.5 overflow-y-auto rounded-[4px] border border-slate-200 bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.16)]",
        isFloatingPositioned
          ? (isPositioned ? "" : "pointer-events-none opacity-0")
          : [
            "absolute top-[calc(100%+0.55rem)] bottom-auto motion-safe:animate-popover-enter",
            align === "right" || isStackedInlinePreview ? "right-0 left-auto" : "left-0 right-auto",
          ],
        popoverClassName,
      ])}
      style={isFloatingPositioned && floatingStyles
        ? {
          ...floatingStyles,
          opacity: isPositioned ? 1 : 0,
        }
        : undefined}
      onKeyDown={handleCalendarKeyDown}
      onMouseDown={(event) => event.preventDefault()}
    >
      <div className="flex flex-wrap items-start justify-between gap-1.5">
        <div className="grid min-w-0 flex-1 gap-1">
          <div className="grid gap-0.5">
            <p id={titleId} className="m-0 text-[0.64rem] font-black uppercase tracking-[0.16em] text-slate-500">Sélection de journée</p>
          </div>

          {dispatchStatusPills.length > 0 ? <DispatchStatusPills pills={dispatchStatusPills} /> : null}
        </div>

        {hiddenClosedCount > 0 || showArchive ? (
          <ActionButton
            size="sm"
            tone="ghost"
            className="h-6! min-h-6! rounded-[4px]! border-slate-300! bg-white! px-2! text-[0.72rem]! text-slate-600! shadow-none! hover:border-slate-400! hover:bg-slate-50! hover:text-slate-900!"
            onClick={() => {
              onToggleArchive?.(!showArchive);
            }}
          >
            {showArchive ? "Masquer les journées fermées" : `Afficher les archives (${hiddenClosedCount})`}
          </ActionButton>
        ) : null}
      </div>

      {errorMessage ? (
        <p className="m-0 rounded-[4px] border border-red-300 bg-white px-2 py-1 text-[0.72rem] leading-4 text-red-800">
          {errorMessage}
        </p>
      ) : null}

      {loadingMessage ? (
        <p className="m-0 rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1 text-[0.72rem] leading-4 text-slate-600">
          {loadingMessage}
        </p>
      ) : null}

      {!loadingMessage && !errorMessage && items.length > 0 ? (
        <>
          <DispatchMonthTabs
            activeMonthKey={activeMonthKey}
            monthGroups={monthGroups}
            onSelectMonthKey={onSelectMonthKey}
          />

          <DispatchCalendarMonthPanel
            activeMonthGroup={activeMonthGroup}
            calendarCells={calendarCells}
            dayButtonRefs={dayButtonRefs}
            minDate={minDate}
            maxDate={maxDate}
            onSelectDispatchDay={onSelectDate}
            todayDispatchDate={todayDispatchDate}
          />

          <div className="grid gap-0.5 rounded-[4px] border border-slate-200 bg-white px-2 py-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <p className="m-0 text-[0.58rem] font-black uppercase tracking-[0.1em] text-slate-500">{hasExplicitSelection ? "Journée active" : "Aucune journée active"}</p>
              <p className="m-0 min-w-0 flex-1 text-[0.76rem] font-semibold tracking-[-0.02em] text-slate-900">
                {hasExplicitSelection ? formatReadableDispatchDate(activeDispatchDate, selectedLabel) : "Choisissez une journée de dispatch"}
              </p>
              {activeStatus ? (
                <span className={joinClasses([
                  "inline-flex items-center rounded-[4px] border px-1.5 py-0.5 text-[0.54rem] font-black uppercase tracking-[0.12em]",
                  activeStatusClassName,
                ])}>
                  {activeStatus.label}
                </span>
              ) : null}
            </div>
            <p className="m-0 text-[0.65rem] leading-4 text-slate-500">{activeSummary}</p>
          </div>
        </>
      ) : null}

      {!loadingMessage && !errorMessage && items.length === 0 ? (
        <p className="m-0 rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1 text-[0.72rem] leading-4 text-slate-600">
          Aucune journée de dispatch disponible.
        </p>
      ) : null}
    </div>
  );
}
